import { Popover } from '@kobalte/core/popover'
import { For, Show, type JSX } from 'solid-js'
import type { CompanySelectorScope } from 'mormaii-web-presentation/company-selector'
import Css from './company-picker.module.scss'
import { Icon } from '../icons'
import { Spinner } from '../ui'

/**
 * O seletor de empresa.
 *
 * Nao e o `Menu` comum: os outros seletores escolhem entre um punhado de valores
 * conhecidos - periodo, metrica -, e este escolhe entre uma quantidade que nao se
 * conhece. Por isso tem CAMPO DE BUSCA e vai ao servidor a cada termo, e por isso
 * tem scope proprio, movido por um presenter.
 *
 * Duas formas, porque ele aparece em dois lugares: `glass` dentro do banner escuro
 * do painel, `field` como campo de filtro numa tela clara.
 *
 * A primeira pagina so e buscada no `onOpen` - abrir a lista e o que a pede. Sem
 * isso toda tela que MOSTRA o seletor pagaria uma consulta que ninguem pediu.
 */
export function CompanyPicker(props: {
    scope: CompanySelectorScope
    variant?: 'glass' | 'field'
    class?: string
}): JSX.Element {
    const glass = () => props.variant === 'glass'

    return (
        <div class={`${Css.field} ${props.class ?? ''}`}>
            <Show when={!glass() && props.scope.label}>{(label) => <span class={Css.label}>{label()}</span>}</Show>

            <Popover gutter={6} onOpenChange={(open) => open && void props.scope.onOpen()}>
                {/*
                    `h-[38px]`: a MESMA altura do `Menu` e do `DateField`. Sem isto o
                    seletor media 36 e os vizinhos 38, e a linha de filtros ficava com
                    um degrau que so se ve quando se olha para ele.
                */}
                <div class={Css.box} classList={{ [Css.glass]: glass() }}>
                    <Popover.Trigger class={Css.trigger}>
                        <Icon name="domain" size={16} class={Css.icon} />
                        <span class={Css.summary}>{props.scope.summary || props.scope.placeholder}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class={Css.arrow}>
                            <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Popover.Trigger>

                    {/*
                        O X fica FORA do gatilho: dentro dele, o clique para limpar
                        abriria a lista no mesmo gesto.
                    */}
                    <Show when={props.scope.hasSelection && props.scope.clearable}>
                        <button
                            type="button"
                            aria-label="Limpar empresa"
                            onClick={() => void props.scope.onClear()}
                            class={Css.clear}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M18 6 6 18M6 6l12 12" stroke-linecap="round" />
                            </svg>
                        </button>
                    </Show>
                </div>

                <Popover.Portal>
                    <Popover.Content class={Css.list}>
                        <div class={Css.search}>
                            <input
                                value={props.scope.term}
                                onInput={(event) => props.scope.onTermChanged(event.currentTarget.value)}
                                placeholder="Buscar empresa…"
                                class={Css.searchInput}
                            />
                            <Show when={props.scope.loading}>
                                <Spinner class={Css.searching} />
                            </Show>
                        </div>

                        <div class={Css.options}>
                            <For
                                each={props.scope.options}
                                fallback={
                                    <p class={Css.empty}>
                                        {props.scope.loading ? 'Buscando…' : 'Nenhuma empresa encontrada'}
                                    </p>
                                }
                            >
                                {/*
                                    `aria-label` explicito em cada opcao: o
                                    `CloseButton` do Kobalte se anuncia como
                                    "Dismiss", e sem isto a lista inteira de empresas
                                    soa igual num leitor de tela.
                                */}
                                {(option) => (
                                    <Popover.CloseButton
                                        aria-label={option.label}
                                        onClick={() => void option.onSelect()}
                                        class={Css.option}
                                        classList={{ [Css.current]: option.current }}
                                    >
                                        <span class={Css.optionText}>{option.label}</span>
                                    </Popover.CloseButton>
                                )}
                            </For>
                        </div>

                        {/*
                            Quantas casaram contra quantas couberam: sem esta linha,
                            quem busca "trans" ve dez e conclui que so ha dez.
                        */}
                        <Show when={props.scope.total > props.scope.options.length}>
                            <p class={Css.total}>
                                {props.scope.total} empresas encontradas. Refine a busca para ver as demais.
                            </p>
                        </Show>
                    </Popover.Content>
                </Popover.Portal>
            </Popover>
        </div>
    )
}
